import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../../../components/ui/tooltip";
import {
  getOpportunities,
  getOpportunityStats,
  getDistinctSetAsides,
  getDistinctNaicsCodes,
  getDistinctDepartments,
  formatDate,
  formatDeadline,
} from "../services/opportunitiesService";
import {
  Search,
  Filter,
  ChevronRight,
  TrendingUp,
  TrendingDown,
  Minus,
  RefreshCw,
  Calendar,
  Building2,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Clock,
  AlertTriangle,
  Shield,
  X,
  Info,
} from "lucide-react";

const SIMILARITY_BAND_CONFIG = {
  high: { variant: "success", label: "High Match", icon: TrendingUp },
  medium: { variant: "warning", label: "Medium", icon: Minus },
  low: { variant: "secondary", label: "Low", icon: TrendingDown },
  none: { variant: "outline", label: "None", icon: null },
};

// Common set-aside codes that might match disabled veteran
const VETERAN_SET_ASIDES = ["SDVOSBC", "SDVOSB", "VSB", "VOSB"];

// Educational info for set-aside types - shown on hover
const SET_ASIDE_INFO = {
  SDVOSB: {
    short: "Competitive",
    long: "Multiple Service-Disabled Veteran-Owned Small Businesses compete for the contract.",
  },
  SDVOSBC: {
    short: "Sole Source",
    long: "Contract awarded directly to one SDVOSB without competition (under threshold limits).",
  },
  VOSB: {
    short: "Veteran-Owned",
    long: "Set aside for Veteran-Owned Small Businesses (not requiring service-connected disability).",
  },
  VSB: {
    short: "Veteran Small Business",
    long: "Reserved for small businesses owned by veterans.",
  },
  "8A": {
    short: "8(a) Program",
    long: "For businesses in SBA's 8(a) Business Development program for disadvantaged businesses.",
  },
  SBA: {
    short: "Small Business",
    long: "Total Small Business Set-Aside - only small businesses can compete.",
  },
  WOSB: {
    short: "Women-Owned",
    long: "Set aside for Women-Owned Small Businesses.",
  },
  EDWOSB: {
    short: "Econ. Disadvantaged Women",
    long: "For Economically Disadvantaged Women-Owned Small Businesses.",
  },
  HUBZone: {
    short: "HUBZone",
    long: "For businesses in Historically Underutilized Business Zones.",
  },
};

export default function OpportunitiesList() {
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastDoc, setLastDoc] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  // Filter options (loaded from data)
  const [setAsideOptions, setSetAsideOptions] = useState([]);
  const [naicsOptions, setNaicsOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  // Active filters
  const [filters, setFilters] = useState({
    searchTerm: "",
    setAside: "",
    naicsCode: "",
    department: "",
    showNewOnly: false,
    showVeteranOnly: false,
    dueSoon: false,
  });

  // Sorting
  const [sortBy, setSortBy] = useState("response_deadline");
  const [sortDirection, setSortDirection] = useState("asc");

  // Load filter options on mount
  useEffect(() => {
    async function loadFilterOptions() {
      try {
        const [setAsides, naics, depts] = await Promise.all([
          getDistinctSetAsides(),
          getDistinctNaicsCodes(),
          getDistinctDepartments(),
        ]);
        setSetAsideOptions(setAsides);
        setNaicsOptions(naics);
        setDepartmentOptions(depts);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    }
    loadFilterOptions();
  }, []);

  const fetchOpportunities = useCallback(async (reset = false) => {
    try {
      setLoading(true);
      setError(null);

      const result = await getOpportunities({
        isNew: filters.showNewOnly ? true : null,
        setAside: filters.setAside || null,
        naicsCode: filters.naicsCode || null,
        department: filters.department || null,
        sortBy,
        sortDirection,
        lastDoc: reset ? null : lastDoc,
      });

      let opps = result.opportunities;

      // Apply veteran filter (client-side since it's a compound check)
      if (filters.showVeteranOnly) {
        opps = opps.filter(opp =>
          VETERAN_SET_ASIDES.some(code =>
            opp.set_aside?.includes(code) ||
            opp.set_aside_description?.toLowerCase().includes("veteran")
          )
        );
      }

      // Apply due soon filter (within 14 days)
      if (filters.dueSoon) {
        const twoWeeksFromNow = new Date();
        twoWeeksFromNow.setDate(twoWeeksFromNow.getDate() + 14);
        opps = opps.filter(opp => {
          if (!opp.response_deadline) return false;
          const deadline = opp.response_deadline.toDate
            ? opp.response_deadline.toDate()
            : new Date(opp.response_deadline);
          return deadline > new Date() && deadline <= twoWeeksFromNow;
        });
      }

      if (reset) {
        setOpportunities(opps);
      } else {
        setOpportunities((prev) => [...prev, ...opps]);
      }

      setLastDoc(result.lastDoc);
      setHasMore(result.hasMore);
    } catch (err) {
      console.error("Error fetching opportunities:", err);
      setError("Failed to load opportunities. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [filters, sortBy, sortDirection, lastDoc]);

  const fetchStats = useCallback(async () => {
    try {
      const statsData = await getOpportunityStats();
      setStats(statsData);
    } catch (err) {
      console.error("Error fetching stats:", err);
    }
  }, []);

  // Fetch data when filters or sort change
  useEffect(() => {
    fetchOpportunities(true);
  }, [filters.showNewOnly, filters.setAside, filters.naicsCode, filters.department, filters.showVeteranOnly, filters.dueSoon, sortBy, sortDirection]);

  useEffect(() => {
    fetchStats();
  }, []);

  const handleRefresh = () => {
    fetchOpportunities(true);
    fetchStats();
  };

  const handleLoadMore = () => {
    if (hasMore && !loading) {
      fetchOpportunities(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDirection(field === "response_deadline" ? "asc" : "desc");
    }
  };

  const clearFilters = () => {
    setFilters({
      searchTerm: "",
      setAside: "",
      naicsCode: "",
      department: "",
      showNewOnly: false,
      showVeteranOnly: false,
      dueSoon: false,
    });
  };

  const hasActiveFilters = filters.setAside || filters.naicsCode || filters.department ||
    filters.showNewOnly || filters.showVeteranOnly || filters.dueSoon;

  // Client-side search filter
  const filteredOpportunities = opportunities.filter((opp) => {
    if (!filters.searchTerm) return true;
    const term = filters.searchTerm.toLowerCase();
    return (
      opp.summary_title?.toLowerCase().includes(term) ||
      opp.title?.toLowerCase().includes(term) ||
      opp.agency_name?.toLowerCase().includes(term) ||
      opp.naics_code?.includes(term) ||
      opp.psc_code?.toLowerCase().includes(term) ||
      opp.solicitation_number?.toLowerCase().includes(term) ||
      opp.set_aside_description?.toLowerCase().includes(term)
    );
  });

  const renderSimilarityBand = (band) => {
    const config = SIMILARITY_BAND_CONFIG[band] || SIMILARITY_BAND_CONFIG.none;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className="gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {config.label}
      </Badge>
    );
  };

  const renderDeadline = (deadline) => {
    const { text, urgent, closed } = formatDeadline(deadline);
    if (closed) {
      return <span className="text-muted-foreground line-through">{text}</span>;
    }
    if (urgent) {
      return (
        <span className="flex items-center gap-1 text-destructive font-medium">
          <AlertTriangle className="h-3 w-3" />
          {text}
        </span>
      );
    }
    return <span>{text}</span>;
  };

  const renderSetAside = (opp) => {
    if (!opp.set_aside && !opp.set_aside_description) {
      return <span className="text-muted-foreground">Full & Open</span>;
    }

    const isVeteran = VETERAN_SET_ASIDES.some(code =>
      opp.set_aside?.includes(code) ||
      opp.set_aside_description?.toLowerCase().includes("veteran")
    );

    // Get tooltip info for this set-aside code
    const info = opp.set_aside ? SET_ASIDE_INFO[opp.set_aside] : null;

    const content = (
      <div className="flex items-center gap-1">
        {isVeteran && <Shield className="h-3 w-3 text-blue-600" />}
        <span className={isVeteran ? "text-blue-600 font-medium" : ""}>
          {opp.set_aside_description || opp.set_aside}
        </span>
        {info && <Info className="h-3 w-3 text-muted-foreground opacity-50" />}
      </div>
    );

    // Wrap with tooltip if we have info
    if (info) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="cursor-help">{content}</div>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs">
            <p className="font-medium">{info.short}</p>
            <p className="text-xs opacity-80">{info.long}</p>
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortDirection === "asc"
      ? <ArrowUp className="h-3 w-3 ml-1" />
      : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  return (
    <TooltipProvider>
    <div className="space-y-6">
      {/* Stats Cards */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Total Opportunities</CardDescription>
              <CardTitle className="text-2xl">{stats.total}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>Active (Open)</CardDescription>
              <CardTitle className="text-2xl text-green-600">{stats.active}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>New This Period</CardDescription>
              <CardTitle className="text-2xl text-blue-600">{stats.new}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-2">
              <CardDescription>High Match</CardDescription>
              <CardTitle className="text-2xl text-purple-600">
                {stats.byBand.high}
              </CardTitle>
            </CardHeader>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters & Search
            </CardTitle>
            <div className="flex gap-2">
              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters}>
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
              <Button variant="outline" size="sm" onClick={handleRefresh}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by title, agency, NAICS, solicitation number, set-aside..."
              value={filters.searchTerm}
              onChange={(e) => setFilters({ ...filters, searchTerm: e.target.value })}
              className="pl-9"
            />
          </div>

          {/* Quick Filters Row */}
          <div className="flex flex-wrap gap-2">
            <Button
              variant={filters.showVeteranOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters({ ...filters, showVeteranOnly: !filters.showVeteranOnly })}
              className={filters.showVeteranOnly ? "bg-blue-600 hover:bg-blue-700" : ""}
            >
              <Shield className="h-4 w-4 mr-2" />
              Veteran Set-Asides
            </Button>

            <Button
              variant={filters.dueSoon ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters({ ...filters, dueSoon: !filters.dueSoon })}
              className={filters.dueSoon ? "bg-orange-600 hover:bg-orange-700" : ""}
            >
              <Clock className="h-4 w-4 mr-2" />
              Due in 14 Days
            </Button>

            <Button
              variant={filters.showNewOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setFilters({ ...filters, showNewOnly: !filters.showNewOnly })}
            >
              New Only
            </Button>
          </div>

          {/* Dropdown Filters */}
          <div className="grid gap-4 md:grid-cols-3">
            <div>
              <label className="text-sm font-medium mb-1 block">Set-Aside Type</label>
              <Select
                value={filters.setAside}
                onValueChange={(value) => setFilters({ ...filters, setAside: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Set-Asides" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Set-Asides</SelectItem>
                  {setAsideOptions.map((opt) => (
                    <SelectItem key={opt.code} value={opt.code}>
                      {opt.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">NAICS Code</label>
              <Select
                value={filters.naicsCode}
                onValueChange={(value) => setFilters({ ...filters, naicsCode: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All NAICS Codes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All NAICS Codes</SelectItem>
                  {naicsOptions.map((code) => (
                    <SelectItem key={code} value={code}>
                      {code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium mb-1 block">Agency/Department</label>
              <Select
                value={filters.department}
                onValueChange={(value) => setFilters({ ...filters, department: value === "all" ? "" : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Agencies" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Agencies</SelectItem>
                  {departmentOptions.map((dept) => (
                    <SelectItem key={dept} value={dept}>
                      {dept}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Opportunities Table */}
      <Card>
        <CardHeader>
          <CardTitle>Opportunities</CardTitle>
          <CardDescription>
            {filteredOpportunities.length} opportunities
            {filters.searchTerm && ` matching "${filters.searchTerm}"`}
            {hasActiveFilters && " (filtered)"}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="text-center py-8 text-destructive">{error}</div>
          )}

          {!error && filteredOpportunities.length === 0 && !loading && (
            <div className="text-center py-8 text-muted-foreground">
              <Building2 className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No opportunities found.</p>
              <p className="text-sm mt-2">
                Try adjusting your filters or run an ingest job from Admin.
              </p>
            </div>
          )}

          {filteredOpportunities.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[300px]">
                        <button
                          className="flex items-center hover:text-foreground"
                          onClick={() => handleSort("title")}
                        >
                          Title
                          <SortIcon field="title" />
                        </button>
                      </TableHead>
                      <TableHead>Set-Aside</TableHead>
                      <TableHead>NAICS</TableHead>
                      <TableHead>Agency</TableHead>
                      <TableHead>
                        <button
                          className="flex items-center hover:text-foreground"
                          onClick={() => handleSort("similarity_score")}
                        >
                          Match
                          <SortIcon field="similarity_score" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          className="flex items-center hover:text-foreground"
                          onClick={() => handleSort("posted_date")}
                        >
                          Posted
                          <SortIcon field="posted_date" />
                        </button>
                      </TableHead>
                      <TableHead>
                        <button
                          className="flex items-center hover:text-foreground"
                          onClick={() => handleSort("response_deadline")}
                        >
                          <Calendar className="h-3 w-3 mr-1" />
                          Deadline
                          <SortIcon field="response_deadline" />
                        </button>
                      </TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredOpportunities.map((opp) => (
                      <TableRow key={opp.id} className="group">
                        <TableCell>
                          <div className="flex items-start gap-2">
                            <div>
                              {opp.is_new && (
                                <Badge variant="default" className="text-xs mb-1">
                                  NEW
                                </Badge>
                              )}
                              <p className="font-medium line-clamp-2">
                                {opp.summary_title || opp.title || "Untitled"}
                              </p>
                              {opp.solicitation_number && (
                                <p className="text-xs text-muted-foreground">
                                  {opp.solicitation_number}
                                </p>
                              )}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[150px]">
                          {renderSetAside(opp)}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs bg-muted px-1 py-0.5 rounded">
                            {opp.naics_code || "N/A"}
                          </code>
                        </TableCell>
                        <TableCell className="max-w-[180px]">
                          <p className="truncate text-sm">{opp.agency_name || "N/A"}</p>
                          {opp.office_name && opp.office_name !== opp.agency_name && (
                            <p className="truncate text-xs text-muted-foreground">
                              {opp.office_name}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          {opp.similarity_band ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help">
                                  {renderSimilarityBand(opp.similarity_band)}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>Score: {Math.round((opp.similarity_score || 0) * 100)}%</p>
                                <p className="text-xs opacity-80">{opp.similar_awards_count || 0} similar awards</p>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-muted-foreground text-xs">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {formatDate(opp.posted_date)}
                        </TableCell>
                        <TableCell>
                          {renderDeadline(opp.response_deadline)}
                        </TableCell>
                        <TableCell>
                          <Link to={`/contracts/opportunities/${opp.id}`}>
                            <Button variant="ghost" size="icon" className="opacity-0 group-hover:opacity-100 transition-opacity">
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {hasMore && (
                <div className="mt-4 text-center">
                  <Button
                    variant="outline"
                    onClick={handleLoadMore}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Load More"}
                  </Button>
                </div>
              )}
            </>
          )}

          {loading && opportunities.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              Loading opportunities...
            </div>
          )}
        </CardContent>
      </Card>
    </div>
    </TooltipProvider>
  );
}
